import React, { useState } from 'react';
import ErrorBoundary from './ErrorBoundary';

// Component that throws an error when rendered
const ComponentWithError: React.FC<{ message: string }> = ({ message }) => {
  throw new Error(message);
};

// Component to simulate async errors
const AsyncErrorComponent: React.FC<{ errorType: string }> = ({ errorType }) => {
  React.useEffect(() => {
    const simulateError = async () => {
      // Simulate a short delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Throw appropriate error based on type
      switch (errorType) {
        case 'network':
          throw new Error('Failed to fetch: Connection refused (ECONNREFUSED)');
        case 'server':
          throw new Error('500 Internal Server Error');
        default:
          throw new Error('Unexpected async error');
      }
    };
    
    simulateError();
  }, [errorType]);
  
  return <div>Loading...</div>;
};

// Main test component
const ErrorBoundaryTester: React.FC = () => {
  const [errorType, setErrorType] = useState<string | null>(null);
  const [errorBoundaryKey, setErrorBoundaryKey] = useState(0);
  
  // Reset the error boundary
  const resetError = () => {
    setErrorType(null);
    setErrorBoundaryKey(prev => prev + 1);
  };
  
  // Trigger a specific error type
  const triggerError = (type: string) => {
    setErrorType(type);
  };
  
  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Error Boundary Tester</h1>
      
      <div className="mb-6">
        <p className="mb-3">Click a button to trigger different types of errors:</p>
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={() => triggerError('component')}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Component Error
          </button>
          <button 
            onClick={() => triggerError('network')}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
          >
            Network Error
          </button>
          <button 
            onClick={() => triggerError('server')}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Server Error
          </button>
          {errorType && (
            <button 
              onClick={resetError}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Reset
            </button>
          )}
        </div>
      </div>
      
      <div className="border p-6 rounded-lg bg-gray-50">
        <ErrorBoundary key={errorBoundaryKey}>
          {!errorType && (
            <div className="p-6 bg-white rounded-lg border text-center">
              <p>This component is wrapped in an ErrorBoundary.</p>
              <p className="text-gray-500">Select an error type above to test the error boundary.</p>
            </div>
          )}
          
          {errorType === 'component' && <ComponentWithError message="This is a component render error" />}
          {errorType === 'network' && <AsyncErrorComponent errorType="network" />}
          {errorType === 'server' && <AsyncErrorComponent errorType="server" />}
        </ErrorBoundary>
      </div>
      
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h2 className="text-lg font-semibold mb-2">How to Use</h2>
        <ul className="list-disc ml-6 space-y-1">
          <li>The <b>Component Error</b> simulates an error during component rendering</li>
          <li>The <b>Network Error</b> simulates a connection refused error</li>
          <li>The <b>Server Error</b> simulates a 500 server error</li>
        </ul>
      </div>
    </div>
  );
};

export default ErrorBoundaryTester; 
